import type pino from 'pino';
import { dev, browser } from '$app/environment';

export type AphroditeLogger = {
  fatal: pino.LogFn;
  error: pino.LogFn;
  warn: pino.LogFn;
  info: pino.LogFn;
  debug: pino.LogFn;
  child: (context: Record<string, unknown>) => AphroditeLogger;
};

let aphroditeLogger: AphroditeLogger | undefined = undefined;
let pinoLogger: pino.Logger | undefined = undefined;
let pinoModule: typeof pino | undefined = undefined;
const _levels = ['debug', 'info', 'warn', 'error', 'fatal'] as const;

let betterStackSourceToken: string | undefined = undefined;

export const setBetterStackSourceToken = (token: string | undefined) =>
  (betterStackSourceToken = token);

const createPinoLogger = async (pinoModule: typeof pino) => {
  const stream = !browser
    ? (await import('pino-pretty')).default({
        levelFirst: true,
        colorize: true,
        ignore: 'time,hostname,pid',
      })
    : undefined;

  return pinoModule(
    dev
      ? stream
      : browser
        ? {}
        : pinoModule.transport({
            targets: [
              ...(betterStackSourceToken
                ? [
                    {
                      target: '@logtail/pino',
                      options: { sourceToken: betterStackSourceToken },
                    },
                  ]
                : [
                    {
                      target: 'pino/file',
                      options: {
                        destination: 1, // This means stdout
                      },
                    },
                  ]),
              {
                level: 'fatal',
                target: 'pino/file',
                options: {
                  destination: 1, // This means stdout
                },
              },
            ],
          }),
  );
};

// This whole dance of lazy loading pino is because we need to make sure that
// pino is loaded after our init-pino-server.ts file has run, which sets up
// the bundler overrides for pino needed for transports to work in pino.
const log = async (level: (typeof _levels)[number], ...args: unknown[]) => {
  if (!pinoModule) {
    pinoModule = (await import('pino')).default;
  }
  if (!pinoLogger) {
    pinoLogger = await createPinoLogger(pinoModule);
  }
  (pinoLogger[level] as (...args: unknown[]) => void).apply(pinoLogger, args);
};

const logger = () => {
  if (!aphroditeLogger) {
    aphroditeLogger = {
      fatal: (...args: unknown[]) => log('fatal', ...args),
      error: (...args: unknown[]) => log('error', ...args),
      warn: (...args: unknown[]) => log('warn', ...args),
      info: (...args: unknown[]) => log('info', ...args),
      debug: (...args: unknown[]) => log('debug', ...args),
      child: (context: Record<string, unknown>) => {
        if (!pinoLogger) {
          throw new Error('Logger not initialized');
        }
        return pinoLogger.child(context) as unknown as AphroditeLogger;
      },
    };
  }
  return aphroditeLogger;
};

export default logger;

export const setLogger = (newLogger: AphroditeLogger) => {
  aphroditeLogger = newLogger;
};

if (!browser) {
  process.on('uncaughtException', (err) => {
    logger().error(err, 'Uncaught exception');
  });
  process.on('unhandledRejection', (err) => {
    logger().error(err as Error, 'Unhandled rejection');
  });
}
